import * as stylex from '@stylexjs/stylex';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { reset as resetAssetGroup } from '@/assetGroupEditor/assetGroup.actions';
import { IconWithSpinner } from '@/core/IconWithSpinner.atom';
import { sqWorkbookStore, sqWorksheetStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { resetItemFinder } from '@/itemFinder/itemFinder.actions';
import { useHasAssetGroups } from '@/licenseManagement/licenseManagement.store';
import { navigateToTreeBuilderPage } from '@/main/routing.utilities';
import { SearchGroupHeader } from '@/search/SearchGroupHeader.atom';
import { SearchResult } from '@/search/SearchResult.molecule';
import { clearSelection, searchOrExplore } from '@/search/search.actions';
import { LIMIT_IN_OVERVIEW, SEARCH_RESULT_TYPES } from '@/search/search.constants';
import { useSearchActions, useSearchSelectedItemIds, useSearchSelectedItemTable } from '@/search/search.store';
import { canModifyWorkbook } from '@/services/authorization.service';
import { TRACK } from '@/track/track.constants';
import { doTrack } from '@/track/track.service';
import { LOAD_STATUS } from '@/workbook/workbook.constants';
import { setDisplayAssetGroupEditor, setDisplayTableDefinitionEditor } from '@/worksheet/worksheet.actions';
import { faExclamationTriangle, faHouseChimney, faQuestionCircle } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Button, Icon } from '@seeqdev/qomponents';
import { colors } from '@seeqdev/qomponents/colors.stylex';
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import type { SearchModes, SearchPanes } from '@/search/search.constants';
import type { SelectedSearchItem } from '@/search/search.types';

export interface SearchGroupProps {
  /** Override the heading layout for embedded search groups. */
  headingXstyle?: stylex.StyleXStyles;
  loadStatus: keyof typeof LOAD_STATUS;
  label: string;
  mode: SearchModes;
  searchMode: SearchModes;
  searchTypes: string[];
  scopeIds: string[];
  restrictExploration: boolean;
  onErrorClick: () => void;
  onClickItem: (item: SelectedSearchItem) => void;
  items: any[];
  pane: SearchPanes;
  indicateSelection?: boolean;
  selectedItemId?: string;
  searchResultIcons: (item: SelectedSearchItem) => Promise<ReactNode>;
  noItemsMessage?: string;
  isSelectingAsset?: boolean;
  predefinedSearchTypes?: string[];
  totalItemsCount?: number;
  onSetModeIconClick?: () => void;
  showHomeIcon?: boolean;
  onHomeIconClick?: () => void;
  hideCreateButton?: boolean;
  /** Template creation is supplied by the template library, outside the reusable search UI. */
  templateCreateButton?: React.ReactNode;
  showPreviewBadge?: boolean;
  showNewBadge?: boolean;
  disableSearchGroup?: boolean;
  tooltipLink?: string;
  onTooltipClicked?: () => void;
}

export const SearchGroup = ({
  headingXstyle,
  items,
  loadStatus,
  label,
  mode,
  pane,
  onErrorClick,
  onClickItem,
  searchResultIcons,
  searchMode,
  searchTypes,
  scopeIds,
  restrictExploration,
  noItemsMessage,
  indicateSelection = false,
  selectedItemId,
  isSelectingAsset = false,
  predefinedSearchTypes,
  totalItemsCount = 0,
  onSetModeIconClick,
  showHomeIcon = false,
  onHomeIconClick,
  showPreviewBadge = false,
  hideCreateButton = false,
  templateCreateButton,
  showNewBadge = false,
  disableSearchGroup = false,
  tooltipLink,
  onTooltipClicked,
}: SearchGroupProps) => {
  const { t } = useTranslation();
  const displayAssetGroupEditor = useFluxPath(sqWorksheetStore, (store) => store.displayAssetGroupEditor);
  const displayScalingTableEditor = useFluxPath(sqWorksheetStore, (store) => store.displayTableDefinitionView);
  const workbookId = useFluxPath(sqWorkbookStore, (store) => store.workbookId);
  const hasAssetGroups = useHasAssetGroups();

  const selectedItemIds = useSearchSelectedItemIds();
  const selectedItemTable = useSearchSelectedItemTable();
  const { setMode } = useSearchActions();
  const showCreateAssetGroupButton =
    searchMode === 'asset-groups' && hasAssetGroups && pane === 'main' && canModifyWorkbook(sqWorkbookStore.workbook);
  const showCreateScalingTableButton =
    !disableSearchGroup &&
    searchMode === 'scaling-tables' &&
    pane === 'main' &&
    canModifyWorkbook(sqWorkbookStore.workbook);
  const showCreateTemplateButton =
    searchMode === 'models' && pane === 'main' && canModifyWorkbook(sqWorkbookStore.workbook) && !hideCreateButton;
  const showItemTableButton =
    searchMode === 'item-tables' && pane === 'main' && canModifyWorkbook(sqWorkbookStore.workbook) && !hideCreateButton;
  const displayHierarchyView = searchMode === 'item-hierarchies' && pane === 'main' && !!selectedItemTable;
  const showCreateHierarchyButton =
    displayHierarchyView && canModifyWorkbook(sqWorkbookStore.workbook) && !hideCreateButton;
  const searchingUsages = mode === 'usages';

  const setModeClick = () => {
    if (onSetModeIconClick) {
      onSetModeIconClick();
    } else {
      setMode(searchMode);
      if (searchMode !== 'usages') {
        void searchOrExplore(pane, searchMode, searchTypes, restrictExploration, scopeIds);
      }
    }
  };

  const renderAssetGroupButton = (
    <div>
      <Button
        testId="createButton"
        label={t('SEARCH_DATA.CREATE')}
        onClick={() => {
          // Start from an empty group: the last saved group stays in the store after Save closes the editor
          resetAssetGroup();
          setDisplayAssetGroupEditor(true);
        }}
        size="xs"
        disabled={displayAssetGroupEditor}
        xstyle={styles.createAction}
      />
    </div>
  );

  const renderScalingTableButton = (
    <div>
      <Button
        testId="scalingTableCreateButton"
        label={t('SEARCH_DATA.CREATE')}
        size="xs"
        onClick={() => {
          doTrack(TRACK.SCALING_TABLE_CONFIGURATION, 'Open Create Item Finder Modal');
          setDisplayTableDefinitionEditor(true);
          resetItemFinder();
        }}
        disabled={displayScalingTableEditor}
        xstyle={styles.createAction}
      />
    </div>
  );

  // Shared by the Item Table and Hierarchy create flows, which both open the Tree Builder page with
  // different navigation params and tracking details.
  const renderTreeBuilderCreateButton = ({
    testId,
    xstyle,
    onCreateClick,
  }: {
    testId: string;
    xstyle: stylex.StyleXStyles;
    onCreateClick: (event: MouseEvent | KeyboardEvent) => void;
  }) => (
    <div>
      <Button testId={testId} label={t('SEARCH_DATA.CREATE')} onClick={onCreateClick} size="xs" xstyle={xstyle} />
    </div>
  );

  const renderItemTableButton = renderTreeBuilderCreateButton({
    testId: 'itemTableCreateButton',
    xstyle: styles.createAction,
    onCreateClick: (event) => {
      doTrack(TRACK.TREE_BUILDER, 'Create Button Clicked');
      navigateToTreeBuilderPage({ scopedTo: workbookId, event });
    },
  });

  const renderTemplateButton = <div>{templateCreateButton}</div>;

  const renderHierarchyButton = renderTreeBuilderCreateButton({
    testId: 'hierarchyCreateButton',
    xstyle: styles.createHierarchyAction,
    onCreateClick: (event) => {
      doTrack(TRACK.TREE_BUILDER, 'Create Hierarchy', { itemTableId: selectedItemTable?.id });
      navigateToTreeBuilderPage({ tableId: selectedItemTable?.id, newHierarchy: true, event });
    },
  });

  const renderButton = () => {
    if (showCreateAssetGroupButton) {
      return renderAssetGroupButton;
    }
    if (showCreateScalingTableButton) {
      return renderScalingTableButton;
    }
    if (showItemTableButton) {
      return renderItemTableButton;
    }
    if (showCreateTemplateButton) {
      return renderTemplateButton;
    }
    if (showCreateHierarchyButton) {
      return renderHierarchyButton;
    }
    return null;
  };

  const nonClickableHeading = (
    <span
      data-testid="nonClickableHeading"
      {...stylex.props(
        styles.heading,
        !headingXstyle && styles.panelHeading,
        headingXstyle,
        styles.nonClickableHeading,
      )}>
      <span>
        <SearchGroupHeader
          testId="nonClickableHeadingLabel"
          showPreviewBadge={showPreviewBadge}
          showNewBadge={showNewBadge}
          disableSearchGroup={disableSearchGroup}
          label={t(`SEARCH_DATA.${label}`)}
        />
        {disableSearchGroup && (
          <span>
            {tooltipLink && (
              <a
                target="_blank"
                {...stylex.props(styles.helpLink)}
                href={tooltipLink}
                onClick={onTooltipClicked}
                rel="noopener">
                <Icon icon={faQuestionCircle} extraClassNames="fa-lg" xstyle={styles.helpIcon} testId="tooltipIcon" />
              </a>
            )}
          </span>
        )}
      </span>
      {showCreateAssetGroupButton && renderAssetGroupButton}
      {showCreateScalingTableButton && renderScalingTableButton}
      {showItemTableButton && renderItemTableButton}
      {showCreateTemplateButton && renderTemplateButton}
      {showCreateHierarchyButton && renderHierarchyButton}
    </span>
  );

  const loadingSpinner = (
    <div {...stylex.props(styles.loading)}>
      <IconWithSpinner spinning={true} large={true} />
      <span {...stylex.props(styles.loadingLabel)}>{t(`SEARCH_DATA.LOADING_${label.replace('.', '_')}`)}</span>
    </div>
  );

  // `loadStatus` is typed as "ERROR" | "LOADED" | "LOADING" | "NOT_LOADED" from keyof typeof LOAD_STATUS. But
  // `SEARCH_RESULT_TYPES.ASSETS` is typed as 'assets', so we need to use `as any` here.
  const isLoadingHierarchies = (loadStatus as any) === SEARCH_RESULT_TYPES.ASSETS && mode === 'item-hierarchies';

  return disableSearchGroup ? (
    <div data-testid="disabledStatus">{nonClickableHeading}</div>
  ) : (
    <div>
      {loadStatus === LOAD_STATUS.LOADING && !searchingUsages && (
        <div data-testid="loadingStatus">
          {nonClickableHeading}
          {loadingSpinner}
        </div>
      )}

      {loadStatus === LOAD_STATUS.ERROR && !searchingUsages && (
        <div data-testid="errorStatus">
          {nonClickableHeading}
          <div {...stylex.props(styles.error)} onClick={onErrorClick}>
            <Icon icon={faExclamationTriangle} type="danger" xstyle={styles.errorIcon} />
            <div {...stylex.props(styles.errorMessage)}>
              {t(`SEARCH_DATA.LOAD_${label.replace('.', '_')}_FAILED`)}
              <br />
              <span {...stylex.props(styles.retry)}>{t('SEARCH_DATA.RETRY')}</span>
            </div>
          </div>
        </div>
      )}

      {items.length === 0 &&
        ((mode === searchMode && !searchingUsages) || mode === 'overview') &&
        noItemsMessage &&
        pane === 'main' && (
          <div>
            {nonClickableHeading}
            {isLoadingHierarchies ? (
              loadingSpinner
            ) : (
              <div {...stylex.props(styles.emptyMessage)}>{t(noItemsMessage)}</div>
            )}
          </div>
        )}

      {items.length > 0 && (mode === searchMode || mode === 'overview') && (
        <div data-testid={`searchGroup-${label}`}>
          {mode === 'overview' && (
            <div
              data-testid="modeSet"
              {...stylex.props(styles.heading, !headingXstyle && styles.panelHeading, headingXstyle)}>
              {showHomeIcon && (
                <div data-testid="homeIconContainer">
                  <Icon
                    icon={faHouseChimney}
                    xstyle={styles.homeIcon}
                    testId="breadcrumbHomeIcon"
                    onClick={onHomeIconClick}
                  />
                  <span {...stylex.props(styles.separator)}>»</span>
                </div>
              )}
              <span {...stylex.props(styles.title)}>
                <SearchGroupHeader
                  showPreviewBadge={showPreviewBadge}
                  showNewBadge={showNewBadge}
                  disableSearchGroup={disableSearchGroup}
                  label={`${t(`SEARCH_DATA.${label}`)} ${totalItemsCount > 0 ? `(${totalItemsCount})` : ''}`}
                  setModeClick={setModeClick}
                />
              </span>
              {renderButton()}
            </div>
          )}
          {mode === 'item-hierarchies' && displayHierarchyView && <>{nonClickableHeading}</>}

          {_.map(items, (item: any) => (
            <SearchResult
              isSelectingAsset={isSelectingAsset}
              searchTypes={predefinedSearchTypes}
              key={item.id}
              item={item}
              items={items}
              searchResultIcons={searchResultIcons}
              itemSelected={_.indexOf(selectedItemIds, item.id) > -1}
              onClickItem={() => {
                clearSelection(); // unselect selected items
                onClickItem(item);
              }}
              isSelected={indicateSelection && selectedItemId === item.id}
              noNavigate={restrictExploration}
            />
          ))}
        </div>
      )}
      {mode === 'overview' && totalItemsCount > LIMIT_IN_OVERVIEW && (
        <Button
          testId="viewMoreButton"
          label={t('SEARCH_DATA.VIEW_MORE')}
          onClick={() => setModeClick()}
          xstyle={styles.viewMore}
        />
      )}
    </div>
  );
};

export const styles = stylex.create({
  heading: { display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  nonClickableHeading: { fontSize: '0.9375rem', cursor: 'auto' },
  panelHeading: {
    paddingTop: '0.9375rem',
    paddingBottom: '0.1875rem',
    cursor: 'pointer',
    color: colors.legacyToolsPrimary,
    borderBottomStyle: 'solid',
    borderBottomWidth: 1.5,
    borderBottomColor: colors.legacyToolsGroupBorder,
  },
  createAction: { marginRight: '0.25rem' },
  createHierarchyAction: { paddingRight: 5, marginRight: 5 },
  helpLink: { marginTop: '0.9375rem' },
  helpIcon: {
    marginLeft: 5,
    color: {
      'default': colors.legacyToolsLink,
      ':hover': colors.legacyToolsPrimary,
      ':focus': colors.legacyToolsPrimary,
    },
  },
  loading: {
    display: 'flex',
    flexDirection: 'row',
    flex: '0 0 auto',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: '0.625rem',
    paddingBottom: '0.625rem',
  },
  loadingLabel: { marginTop: '0.3125rem', marginBottom: '0.3125rem', marginLeft: '0.3125rem' },
  error: {
    display: 'flex',
    flexDirection: 'row',
    color: colors.borderInvalid,
    cursor: 'pointer',
    paddingTop: '0.1875rem',
    paddingBottom: '0.375rem',
  },
  errorIcon: { alignSelf: 'center', paddingLeft: 5, paddingRight: 10, paddingTop: 2 },
  errorMessage: { flex: '1 1 auto', overflow: 'hidden', paddingLeft: '0.3125rem' },
  retry: { textDecoration: 'underline' },
  emptyMessage: { margin: '0.3125rem' },
  homeIcon: { paddingLeft: 5, paddingRight: 2 },
  separator: { paddingRight: '0.125rem' },
  title: {
    flex: '1 1 auto',
    overflow: 'hidden',
    fontSize: '0.9375rem',
    color: {
      'default': colors.legacyToolsLink,
      ':hover': colors.legacyToolsPrimary,
      ':focus': colors.legacyToolsPrimary,
    },
  },
  viewMore: {
    borderWidth: 0,
    paddingLeft: '0.3125rem',
    paddingRight: 0,
    paddingTop: 0,
    paddingBottom: 0,
    width: 'fit-content',
    color: colors.legacyToolsPrimary,
    backgroundColor: { 'default': null, ':hover': 'transparent' },
  },
});
